import React, { useEffect, useState, useRef } from 'react';
import './css/BlogSection.css';

const BlogSection = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const timeoutRef = useRef(null);
    const [visibleCards, setVisibleCards] = useState(2); // Show 2 blogs as requested

    const resetTimeout = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setVisibleCards(1);
            } else {
                setVisibleCards(2);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchPosts = async () => {
            setLoading(true);
            try {
                const blogUrl = process.env.REACT_APP_BLOG_URL;
                const response = await fetch(`${blogUrl}/wp-json/wp/v2/posts?_embed&per_page=10`);
                const data = await response.json();
                
                let formattedPosts = data.map(post => {
                    // Try to get featured media
                    let imageUrl = post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
                    
                    // Fallback: extract the first image from the post content if no featured image is set
                    if (!imageUrl && post.content?.rendered) {
                        const imgRegex = /<img[^>]+src="([^">]+)"/g;
                        const match = imgRegex.exec(post.content.rendered);
                        if (match && match[1]) {
                            imageUrl = match[1];
                        }
                    }

                    return {
                        id: post.id,
                        title: post.title.rendered,
                        excerpt: post.excerpt.rendered.replace(/<[^>]+>/g, '').substring(0, 100) + '...',
                        url: post.link,
                        date: post.date,
                        image: imageUrl || '/assets/images/blog-placeholder.jpg'
                    };
                });

                // If only 1 post exists, duplicate it for UI demonstration if user wants to see 2
                if (formattedPosts.length === 1) {
                    formattedPosts.push({ ...formattedPosts[0], id: 'mock-1' });
                }
                
                setPosts(formattedPosts);
            } catch (error) {
                console.error("Error fetching blog posts:", error);
                setPosts([]);
            }
            setLoading(false);
        };
        fetchPosts();
    }, []);

    useEffect(() => {
        resetTimeout();
        // Only swipe if there are more posts than visible cards
        if (posts.length > visibleCards) {
            timeoutRef.current = setTimeout(() => {
                setCurrentIndex((prev) => (prev >= posts.length - visibleCards ? 0 : prev + 1));
            }, 3000);
        }
        return () => resetTimeout();
    }, [currentIndex, posts, visibleCards]);

    // Removed unused handlePrev and handleNext navigation functions

    if (loading) return (
        <div className="blog-section">
            <h2 className="section-title">Latest Blog Posts</h2>
            <div className="loader"></div>
        </div>
    );

    if (posts.length === 0) return null;

    return (
        <section className="blog-section">
            <div className="container">
                <h2 className="section-title">Insights & Updates</h2>
                
                <div className="blog-slider-container">
                    <div className="blog-viewport">
                        <div 
                            className="blog-track" 
                            style={{ transform: `translateX(-${currentIndex * (100 / visibleCards)}%)` }}
                        >
                            {posts.map((post) => (
                                <div className="blog-card-item" key={post.id} style={{ flex: `0 0 ${100 / visibleCards}%` }}>
                                    <div className="blog-simple-card" onClick={() => window.open(post.url, '_blank')}>
                                        <div className="blog-img-box">
                                            <img src={post.image} alt={post.title} />
                                        </div>
                                        <div className="blog-text-box">
                                            <h3>{post.title}</h3>
                                            <p>{post.excerpt}</p>
                                            <div className="blog-card-footer">
                                                <span>{new Date(post.date).toLocaleDateString()}</span>
                                                <span className="read-more">Read More</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="blog-footer">
                    <button className="btn-show-all" onClick={() => window.open(process.env.REACT_APP_BLOG_URL, '_blank')}>
                        Show More Posts
                    </button>
                </div>
            </div>
        </section>
    );
};

export default BlogSection;
